import {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  deleteCourseNotification,
  fetchCourseNotificationHistory,
  fetchNotificationRoster,
  sendCourseNotification
} from '../../../utils/courseNotificationAdmin';


function studentLabel(
  student
) {
  const name =
    student?.englishName ||
    student?.chineseName ||
    'Unnamed student';

  const code =
    student?.studentCode ||
    '';

  const chinese =
    student?.chineseName &&
    student?.chineseName !==
      name
      ? student.chineseName
      : '';

  return [
    code,
    name,
    chinese
  ]
    .filter(Boolean)
    .join(' · ');
}


function formatDateTime(
  value
) {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  return date.toLocaleString(
    'en-GB',
    {
      day:
        'numeric',

      month:
        'short',

      year:
        'numeric',

      hour:
        '2-digit',

      minute:
        '2-digit'
    }
  );
}


export default function
CourseNotifications({
  course,
  session
}) {
  const [
    notifications,
    setNotifications
  ] =
    useState([]);

  const [
    message,
    setMessage
  ] =
    useState('');

  const [
    targetMode,
    setTargetMode
  ] =
    useState('all');

  const [
    roster,
    setRoster
  ] =
    useState([]);

  const [
    selectedIds,
    setSelectedIds
  ] =
    useState([]);

  const [
    search,
    setSearch
  ] =
    useState('');

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    rosterLoading,
    setRosterLoading
  ] =
    useState(false);

  const [
    sending,
    setSending
  ] =
    useState(false);

  const [
    deletingId,
    setDeletingId
  ] =
    useState(null);

  const [
    error,
    setError
  ] =
    useState('');

  const [
    success,
    setSuccess
  ] =
    useState('');


  async function loadHistory() {
    const rows =
      await fetchCourseNotificationHistory(
        session,
        course.id
      );

    setNotifications(
      rows
    );
  }


  useEffect(() => {
    let cancelled =
      false;

    async function load() {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const rows =
          await fetchCourseNotificationHistory(
            session,
            course.id
          );

        if (!cancelled) {
          setNotifications(
            rows
          );
        }

      } catch (err) {
        console.error(
          'Failed to load notifications:',
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
            'Could not load notifications.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }


    setTargetMode(
      'all'
    );

    setRoster(
      []
    );

    setSelectedIds(
      []
    );

    setSearch(
      ''
    );

    load();


    return () => {
      cancelled =
        true;
    };
  }, [
    course.id,
    session
  ]);


  useEffect(() => {
    if (
      targetMode !==
        'individual' ||
      !course.id
    ) {
      return undefined;
    }

    let cancelled =
      false;

    async function loadRoster() {
      setRosterLoading(
        true
      );

      setError('');

      try {
        const rows =
          await fetchNotificationRoster(
            session,
            course.id
          );

        if (!cancelled) {
          setRoster(
            rows
          );
        }

      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
            'Could not load the course roster.'
          );
        }

      } finally {
        if (!cancelled) {
          setRosterLoading(
            false
          );
        }
      }
    }


    loadRoster();


    return () => {
      cancelled =
        true;
    };
  }, [
    targetMode,
    course.id,
    session
  ]);


  const selectedSet =
    useMemo(
      () =>
        new Set(
          selectedIds.map(
            String
          )
        ),
      [
        selectedIds
      ]
    );


  const visibleStudents =
    useMemo(
      () => {
        const needle =
          String(
            search || ''
          )
            .trim()
            .toLowerCase();

        if (!needle) {
          return roster;
        }

        return roster.filter(
          student =>
            [
              student.studentCode,
              student.englishName,
              student.chineseName
            ]
              .filter(Boolean)
              .some(
                value =>
                  String(value)
                    .toLowerCase()
                    .includes(
                      needle
                    )
              )
        );
      },
      [
        roster,
        search
      ]
    );


  function toggleStudent(
    studentId
  ) {
    const id =
      String(
        studentId
      );

    setSelectedIds(
      current => {
        const next =
          new Set(
            current.map(
              String
            )
          );

        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }

        return Array.from(
          next
        );
      }
    );

    setSuccess('');
  }


  function selectVisible() {
    setSelectedIds(
      current => {
        const next =
          new Set(
            current.map(
              String
            )
          );

        visibleStudents.forEach(
          student =>
            next.add(
              String(
                student.studentId
              )
            )
        );

        return Array.from(
          next
        );
      }
    );
  }


  async function handleSend(
    event
  ) {
    event.preventDefault();

    const trimmed =
      message.trim();

    if (
      !trimmed ||
      sending ||
      (
        targetMode ===
          'individual' &&
        selectedIds.length === 0
      )
    ) {
      return;
    }


    setSending(true);
    setError('');
    setSuccess('');


    try {
      const result =
        await sendCourseNotification(
          session,
          {
            courseId:
              course.id,

            message:
              trimmed,

            targetMode,

            studentIds:
              selectedIds
          }
        );


      setMessage('');

      if (
        targetMode ===
        'individual'
      ) {
        setSelectedIds(
          []
        );
      }


      await loadHistory();


      const sent =
        Number(
          result?.push?.sent ||
          0
        );

      const failed =
        Number(
          result?.push?.failed ||
          0
        );


      const audience =
        targetMode ===
          'individual'
          ? String(
              result
                ?.recipientCount ||
              selectedIds.length
            ) +
            ' selected student' +
            (
              (
                result
                  ?.recipientCount ||
                selectedIds.length
              ) === 1
                ? ''
                : 's'
            )
          : 'all students';


      if (
        result?.push?.ok ===
        false
      ) {
        setSuccess(
          'Notification saved for ' +
          audience +
          '. Push delivery could not be started.'
        );

      } else {
        setSuccess(
          'Notification sent to ' +
          audience +
          '. Push delivered to ' +
          sent +
          ' device' +
          (
            sent === 1
              ? ''
              : 's'
          ) +
          (
            failed > 0
              ? '; ' +
                failed +
                ' failed'
              : ''
          ) +
          '.'
        );
      }

    } catch (err) {
      console.error(
        'Failed to send notification:',
        err
      );

      setError(
        err?.message ||
        'Could not send notification.'
      );

    } finally {
      setSending(false);
    }
  }


  async function handleDelete(
    notification
  ) {
    if (
      !window.confirm(
        'Delete this notification? Students will no longer see it in the app.'
      )
    ) {
      return;
    }


    setDeletingId(
      notification.id
    );

    setError('');
    setSuccess('');


    try {
      await deleteCourseNotification(
        session,
        course.id,
        notification.id
      );

      setNotifications(
        current =>
          current.filter(
            item =>
              item.id !==
              notification.id
          )
      );

      setSuccess(
        'Notification deleted.'
      );

    } catch (err) {
      setError(
        err?.message ||
        'Could not delete notification.'
      );

    } finally {
      setDeletingId(
        null
      );
    }
  }


  const courseName =
    course.course_titles?.name ||
    course.name ||
    'this course';


  return (
    <div>
      <h2 style={styles.heading}>
        Notifications
      </h2>

      <p style={styles.help}>
        Send an in-app notification to students in{' '}
        <strong>
          {courseName}
        </strong>
        . Students who have course push notifications enabled
        will also receive a push notification.
      </p>


      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}


      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}


      <form
        onSubmit={handleSend}
        style={styles.form}
      >
        <label style={styles.label}>
          Recipients
        </label>

        <div style={styles.modeRow}>
          <button
            type="button"
            style={{
              ...styles.modeButton,
              ...(
                targetMode ===
                  'all'
                  ? styles
                      .modeButtonActive
                  : {}
              )
            }}
            onClick={() => {
              setTargetMode(
                'all'
              );

              setSuccess('');
            }}
          >
            All
          </button>

          <button
            type="button"
            style={{
              ...styles.modeButton,
              ...(
                targetMode ===
                  'individual'
                  ? styles
                      .modeButtonActive
                  : {}
              )
            }}
            onClick={() => {
              setTargetMode(
                'individual'
              );

              setSuccess('');
            }}
          >
            Individual
          </button>
        </div>


        {targetMode ===
          'individual' && (
          <div style={styles.recipientPanel}>
            <input
              type="search"
              style={styles.search}
              value={search}
              onChange={
                event =>
                  setSearch(
                    event
                      .target
                      .value
                  )
              }
              placeholder="Search by student ID, English name, or Chinese name"
            />

            <div style={styles.selectionBar}>
              <span>
                {selectedIds.length}{' '}
                selected
              </span>

              <span style={styles.selectionActions}>
                <button
                  type="button"
                  style={styles.textButton}
                  onClick={
                    selectVisible
                  }
                  disabled={
                    visibleStudents
                      .length === 0
                  }
                >
                  Select all visible
                </button>

                <button
                  type="button"
                  style={styles.textButton}
                  onClick={() =>
                    setSelectedIds(
                      []
                    )
                  }
                  disabled={
                    selectedIds
                      .length === 0
                  }
                >
                  Clear
                </button>
              </span>
            </div>

            {rosterLoading ? (
              <div style={styles.muted}>
                Loading students...
              </div>

            ) : visibleStudents
                .length === 0 ? (
              <div style={styles.muted}>
                No students match this search.
              </div>

            ) : (
              <div style={styles.studentList}>
                {visibleStudents.map(
                  student => {
                    const id =
                      String(
                        student
                          .studentId
                      );

                    return (
                      <label
                        key={id}
                        style={
                          styles.studentRow
                        }
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedSet
                              .has(id)
                          }
                          onChange={() =>
                            toggleStudent(
                              id
                            )
                          }
                        />

                        <span>
                          {studentLabel(
                            student
                          )}
                        </span>
                      </label>
                    );
                  }
                )}
              </div>
            )}
          </div>
        )}


        <label
          style={styles.label}
          htmlFor="course-notification-message"
        >
          Notification
        </label>

        <textarea
          id="course-notification-message"
          style={styles.textarea}
          value={message}
          onChange={
            event =>
              setMessage(
                event.target.value
              )
          }
          placeholder="Write a notification for students..."
          rows={5}
        />


        <div style={styles.actions}>
          <button
            type="submit"
            style={{
              ...styles.primaryBtn,
              ...(
                (
                  !message.trim() ||
                  sending ||
                  (
                    targetMode ===
                      'individual' &&
                    selectedIds
                      .length === 0
                  )
                )
                  ? styles
                      .disabledBtn
                  : {}
              )
            }}
            disabled={
              !message.trim() ||
              sending ||
              (
                targetMode ===
                  'individual' &&
                selectedIds.length ===
                  0
              )
            }
          >
            {sending
              ? 'Sending...'
              : 'Send Notification'}
          </button>
        </div>
      </form>


      <div style={styles.historyHeader}>
        <h3 style={styles.subheading}>
          Sent Notifications
        </h3>
      </div>


      {loading ? (
        <div style={styles.muted}>
          Loading notifications...
        </div>

      ) : notifications.length ===
          0 ? (
        <div style={styles.empty}>
          No notifications have been sent for this course.
        </div>

      ) : (
        <div style={styles.list}>
          {notifications.map(
            notification => (
              <div
                key={
                  notification.id
                }
                style={styles.item}
              >
                <div style={styles.itemMain}>
                  <div style={styles.message}>
                    {notification.message}
                  </div>

                  <div style={styles.meta}>
                    {notification
                      .target_mode ===
                    'individual'
                      ? (
                          notification
                            .recipientCount ||
                          0
                        ) +
                        ' individual student' +
                        (
                          Number(
                            notification
                              .recipientCount ||
                            0
                          ) === 1
                            ? ''
                            : 's'
                        )
                      : notification
                          .target_mode ===
                        'group'
                        ? 'Group'
                        : 'All students'}

                    {' · '}

                    {formatDateTime(
                      notification
                        .created_at
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  style={{
                    ...styles.deleteBtn,
                    ...(
                      deletingId ===
                        notification.id
                        ? styles
                            .disabledBtn
                        : {}
                    )
                  }}
                  disabled={
                    deletingId ===
                    notification.id
                  }
                  onClick={() =>
                    handleDelete(
                      notification
                    )
                  }
                >
                  {deletingId ===
                    notification.id
                    ? 'Deleting...'
                    : 'Delete'}
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}


const styles = {
  heading: {
    margin:
      '0 0 8px',
    fontSize:
      '22px',
    color:
      '#1f2933'
  },

  help: {
    margin:
      '0 0 20px',
    color:
      '#66788a',
    fontSize:
      '14px',
    lineHeight:
      '1.5'
  },

  form: {
    border:
      '1px solid #d7dee5',
    borderRadius:
      '8px',
    padding:
      '18px',
    background:
      '#f9fbfc'
  },

  label: {
    display:
      'block',
    marginBottom:
      '7px',
    marginTop:
      '14px',
    color:
      '#334e68',
    fontSize:
      '13px',
    fontWeight:
      '700'
  },

  modeRow: {
    display:
      'flex',
    gap:
      '6px'
  },

  modeButton: {
    border:
      '1px solid #cbd5df',
    borderRadius:
      '7px',
    background:
      'white',
    color:
      '#526779',
    padding:
      '8px 14px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  modeButtonActive: {
    background:
      '#215b7d',
    borderColor:
      '#215b7d',
    color:
      'white'
  },

  recipientPanel: {
    marginTop:
      '12px',
    border:
      '1px solid #d7dee5',
    borderRadius:
      '7px',
    padding:
      '12px',
    background:
      'white'
  },

  search: {
    width:
      '100%',
    boxSizing:
      'border-box',
    border:
      '1px solid #cbd5df',
    borderRadius:
      '6px',
    padding:
      '9px 10px',
    fontSize:
      '13px'
  },

  selectionBar: {
    display:
      'flex',
    alignItems:
      'center',
    justifyContent:
      'space-between',
    gap:
      '12px',
    margin:
      '10px 0',
    fontSize:
      '12px',
    color:
      '#627d98'
  },

  selectionActions: {
    display:
      'flex',
    gap:
      '10px'
  },

  textButton: {
    border:
      0,
    background:
      'transparent',
    color:
      '#215b7d',
    padding:
      0,
    fontSize:
      '12px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  studentList: {
    maxHeight:
      '280px',
    overflowY:
      'auto',
    borderTop:
      '1px solid #edf1f5'
  },

  studentRow: {
    display:
      'flex',
    alignItems:
      'center',
    gap:
      '9px',
    padding:
      '8px 3px',
    borderBottom:
      '1px solid #edf1f5',
    fontSize:
      '13px',
    color:
      '#334e68',
    cursor:
      'pointer'
  },

  textarea: {
    width:
      '100%',
    boxSizing:
      'border-box',
    resize:
      'vertical',
    minHeight:
      '110px',
    border:
      '1px solid #cbd5df',
    borderRadius:
      '7px',
    padding:
      '11px 12px',
    background:
      'white',
    color:
      '#1f2933',
    fontFamily:
      'inherit',
    fontSize:
      '14px',
    lineHeight:
      '1.5'
  },

  actions: {
    display:
      'flex',
    justifyContent:
      'flex-end',
    marginTop:
      '12px'
  },

  primaryBtn: {
    border:
      'none',
    borderRadius:
      '7px',
    background:
      '#215b7d',
    color:
      'white',
    padding:
      '10px 15px',
    fontSize:
      '13px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  disabledBtn: {
    opacity:
      0.55,
    cursor:
      'default'
  },

  historyHeader: {
    marginTop:
      '26px',
    marginBottom:
      '10px'
  },

  subheading: {
    margin:
      0,
    color:
      '#243b53',
    fontSize:
      '16px'
  },

  list: {
    border:
      '1px solid #d7dee5',
    borderRadius:
      '8px',
    overflow:
      'hidden'
  },

  item: {
    display:
      'flex',
    alignItems:
      'flex-start',
    justifyContent:
      'space-between',
    gap:
      '18px',
    padding:
      '14px 16px',
    borderBottom:
      '1px solid #e7edf2',
    background:
      'white'
  },

  itemMain: {
    minWidth:
      0,
    flex:
      1
  },

  message: {
    color:
      '#243b53',
    fontSize:
      '14px',
    lineHeight:
      '1.5',
    whiteSpace:
      'pre-wrap',
    overflowWrap:
      'anywhere'
  },

  meta: {
    marginTop:
      '6px',
    color:
      '#829ab1',
    fontSize:
      '11px'
  },

  deleteBtn: {
    flexShrink:
      0,
    border:
      '1px solid #d7dee5',
    borderRadius:
      '6px',
    background:
      'white',
    color:
      '#a33',
    padding:
      '7px 10px',
    fontSize:
      '12px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  error: {
    marginBottom:
      '14px',
    border:
      '1px solid #f0c8c5',
    borderRadius:
      '7px',
    background:
      '#fff1f0',
    color:
      '#a33',
    padding:
      '10px 12px',
    fontSize:
      '13px'
  },

  success: {
    marginBottom:
      '14px',
    border:
      '1px solid #b9dfc6',
    borderRadius:
      '7px',
    background:
      '#f0fff4',
    color:
      '#25633b',
    padding:
      '10px 12px',
    fontSize:
      '13px'
  },

  muted: {
    color:
      '#66788a',
    fontSize:
      '13px',
    padding:
      '8px 0'
  },

  empty: {
    border:
      '1px dashed #cbd5df',
    borderRadius:
      '7px',
    padding:
      '16px',
    color:
      '#66788a',
    fontSize:
      '13px'
  }
};
